import { keyframes } from '@emotion/react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { getBowImageUrl, getCollectibleImageUrl } from '../../../../post-wrap/post-wrap-images';
import { useUserContentDismissal } from '../../../../profile/user-content-dismissal';

const revealGiftPresent = keyframes`
	0% { opacity: 0; transform: scale(0.9); }
	100% { opacity: 1; transform: scale(1); }
`;

const unwrapGift = keyframes`
	0% { transform: scale(1) rotate(0deg); opacity: 1; }
	30% { transform: scale(1.1) rotate(-3deg); }
	50% { transform: scale(1.15) rotate(3deg); }
	70% { transform: scale(1.2) rotate(-2deg); }
	100% { transform: scale(1.5) rotate(0deg); opacity: 0; }
`;

const shimmerGift = keyframes`
	0% { background-position: -200% center; }
	100% { background-position: 200% center; }
`;

interface GiftWrappedBonusProps {
	bonus: Bonus;
	isUserReceiver: boolean;
	children: ReactNode;
}

const localStorageKey = (bonusId: string) => `gift-wrapped-dismissed-${bonusId}`;

const readLocalDismissal = (bonusId: string): boolean => {
	try {
		return localStorage.getItem(localStorageKey(bonusId)) === 'true';
	} catch {
		return false;
	}
};

const writeLocalDismissal = (bonusId: string): void => {
	try {
		localStorage.setItem(localStorageKey(bonusId), 'true');
	} catch {
		// Ignore — storage may be unavailable in some private browsing modes
	}
};

export const GiftWrappedBonus: FC<GiftWrappedBonusProps> = ({ bonus, isUserReceiver, children }) => {
	const { t } = useTranslation('recognition');
	const [isUnwrapping, setIsUnwrapping] = useState(false);
	const [hasUnwrapped, setHasUnwrapped] = useState(false);

	const style = bonus.visual_state?.gift_wrap_style ?? '';
	const color = bonus.visual_state?.gift_wrap_color ?? '';
	const wrappedImageUrl = getCollectibleImageUrl(style, color, 1);
	const bowImageUrl = getBowImageUrl(color);

	// Check localStorage synchronously so we never flash the wrapped state for
	// a bonus the user has already unwrapped in a previous session.
	const isLocallyDismissed = isUserReceiver && readLocalDismissal(bonus.id);

	const { loading, data, updateUserContentDismissal } = useUserContentDismissal({
		name: `gift-wrapped-${bonus.id}`,
		namespace: 'free-rewards',
		enabled: !!bonus.visual_state?.gift_wrapped && isUserReceiver && !isLocallyDismissed,
	});

	// If the API confirms dismissal but localStorage doesn't have it yet (e.g. unwrapped
	// on another device), backfill localStorage so future loads on this device are instant.
	useEffect(() => {
		if (!isLocallyDismissed && data?.dismissed_at) {
			writeLocalDismissal(bonus.id);
		}
	}, [bonus.id, isLocallyDismissed, data?.dismissed_at]);

	if (!bonus.visual_state?.gift_wrapped) {
		return <>{children}</>;
	}

	// Dismissed if local cache says so, or once the API confirms it
	const isDismissed = isLocallyDismissed || (!loading && !!data?.dismissed_at);

	// Show overlay when: receiver, not dismissed, not yet unwrapped in this session
	const showOverlay = isUserReceiver && !isDismissed && !hasUnwrapped && wrappedImageUrl;

	const handleUnwrap = () => {
		setIsUnwrapping(true);
		setTimeout(() => {
			writeLocalDismissal(bonus.id);
			updateUserContentDismissal();
			setHasUnwrapped(true);
		}, 600);
	};

	if (!showOverlay) {
		return <Box animation={hasUnwrapped ? `${revealGiftPresent} 0.4s ease-out` : undefined}>{children}</Box>;
	}

	return (
		<Box
			position="relative"
			borderRadius="lg"
			overflow="hidden"
			aspectRatio="620 / 427"
			role="region"
			aria-label={t('gift_wrap.wrapped_aria_label', { defaultValue: 'You received a gift-wrapped recognition! Click unwrap to reveal.' })}
		>
			{/* The actual bonus content (hidden from assistive tech while wrapped) */}
			<Box visibility="hidden" aria-hidden="true">
				{children}
			</Box>

			{/* Wrapping paper overlay */}
			<Box
				position="absolute"
				top={0}
				left={0}
				right={0}
				bottom={0}
				backgroundImage={`url(${wrappedImageUrl})`}
				backgroundSize="cover"
				backgroundPosition="center"
				borderRadius="lg"
				zIndex={1}
				role="img"
				aria-label={t('gift_wrap.wrapping_paper_aria', { defaultValue: 'Gift wrapping paper' })}
				animation={isUnwrapping ? `${unwrapGift} 0.6s ease-out forwards` : undefined}
			>
				{/* Subtle shimmer effect - purely decorative */}
				<Box
					position="absolute"
					top={0}
					left={0}
					right={0}
					bottom={0}
					background="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)"
					backgroundSize="200% 100%"
					borderRadius="lg"
					pointerEvents="none"
					aria-hidden="true"
					animation={`${shimmerGift} 3s ease-in-out infinite`}
				/>

				{/* Bow */}
				{bowImageUrl && (
					<Box
						as="img"
						src={bowImageUrl}
						position="absolute"
						top={0}
						left={0}
						width="100%"
						height="100%"
						objectFit="cover"
						pointerEvents="none"
						aria-hidden="true"
					/>
				)}

				{/* Unwrap button */}
				<Flex position="absolute" bottom={4} right={4} alignItems="center" gap={2}>
					<Button
						size="md"
						bg="white"
						color="gray.800"
						fontWeight="medium"
						borderRadius="md"
						boxShadow="lg"
						px={6}
						py={4}
						transform="rotate(-4deg)"
						onClick={handleUnwrap}
						isLoading={isUnwrapping}
						aria-label={t('gift_wrap.unwrap_button_aria', { defaultValue: 'Unwrap this gift to reveal your recognition' })}
						aria-busy={isUnwrapping}
						_hover={{
							bg: 'gray.100',
							transform: 'rotate(-4deg) scale(1.05)',
						}}
						_active={{
							bg: 'gray.200',
							transform: 'rotate(-4deg) scale(0.98)',
						}}
						transition="all 0.2s"
					>
						{t('gift_wrap.unwrap_button', { defaultValue: 'Unwrap me!' })}
					</Button>
				</Flex>
			</Box>
		</Box>
	);
};
